import { BUSINESS, LOGO_URL } from "@/data";
import * as Icons from "lucide-react";

export const CallButton = ({ children = "Call Now", className = "", testid = "btn-call", variant = "primary", size = "lg" }) => {
  const variants = {
    primary: "bg-brand-red hover:brightness-110 text-white animate-pulse-red",
    secondary: "bg-white hover:bg-slate-50 text-brand-navy border border-slate-200 shadow-sm",
    outline: "bg-transparent hover:bg-slate-50 text-brand-navy border border-slate-300",
  };
  const sizes = {
    sm: "py-2 px-4 text-sm",
    md: "py-2.5 px-5 text-sm",
    lg: "py-3.5 px-6 text-base",
  };
  return (
    <a
      href={`tel:${BUSINESS.phoneTel}`}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 font-bold font-display rounded-full transition-all active:scale-95 ${variants[variant]} ${sizes[size]} ${className}`}
    >
      <Icons.Phone className="w-4 h-4" />
      {children}
    </a>
  );
};

export const DirectionsButton = ({ children = "Get Directions", className = "", testid = "btn-directions", variant = "secondary", size = "lg" }) => {
  const variants = {
    primary: "bg-brand-blue hover:brightness-110 text-white glow-blue",
    secondary: "bg-white hover:bg-slate-50 text-brand-navy border border-slate-200 shadow-sm",
    outline: "bg-transparent hover:bg-slate-50 text-brand-navy border border-slate-300",
  };
  const sizes = {
    sm: "py-2 px-4 text-sm",
    md: "py-2.5 px-5 text-sm",
    lg: "py-3.5 px-6 text-base",
  };
  return (
    <a
      href={BUSINESS.directionsUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 font-bold font-display rounded-full transition-all active:scale-95 ${variants[variant]} ${sizes[size]} ${className}`}
    >
      <Icons.MapPin className="w-4 h-4" />
      {children}
    </a>
  );
};

export const QuoteButton = ({ children = "Get a Quote", className = "", testid = "btn-quote", onClick, variant = "outline", size = "lg" }) => {
  const variants = {
    primary: "bg-brand-red hover:brightness-110 text-white animate-pulse-red",
    secondary: "bg-white hover:bg-slate-50 text-brand-navy border border-slate-200 shadow-sm",
    outline: "bg-transparent hover:bg-slate-50 text-brand-navy border border-slate-300",
  };
  const sizes = {
    sm: "py-2 px-4 text-sm",
    md: "py-2.5 px-5 text-sm",
    lg: "py-3.5 px-6 text-base",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 font-bold font-display rounded-full transition-all active:scale-95 ${variants[variant]} ${sizes[size]} ${className}`}
    >
      <Icons.MessageSquareQuote className="w-4 h-4" />
      {children}
    </button>
  );
};

export const InstagramButton = ({ className = "", testid = "btn-instagram", children = "Message on Instagram", size = "lg" }) => {
  const sizes = {
    sm: "py-2 px-4 text-sm",
    md: "py-2.5 px-5 text-sm",
    lg: "py-3.5 px-6 text-base",
  };
  return (
    <a
      href={BUSINESS.instagram}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 font-bold font-display rounded-full transition-all active:scale-95 bg-white hover:bg-slate-50 text-brand-navy border border-slate-200 shadow-sm ${sizes[size]} ${className}`}
    >
      <Icons.Instagram className="w-4 h-4" />
      {children}
    </a>
  );
};

export const SectionHeading = ({ eyebrow, title, sub, align = "left", testid }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`} data-testid={testid}>
    {eyebrow && (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 mb-4 ${align === "center" ? "" : ""}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-brand-red animate-pulse" />
        <span className="text-xs uppercase tracking-[0.16em] font-bold" style={{ color: "var(--brand-red-dark)" }}>{eyebrow}</span>
      </div>
    )}
    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-brand-navy">
      {title}
    </h2>
    {sub && <p className="mt-5 text-base md:text-lg text-slate-600 max-w-2xl">{sub}</p>}
  </div>
);

// Logo lockup — official Your Phone Plug logo
export const Logo = ({ size = "md", className = "" }) => {
  const dims = size === "sm" ? "w-9 h-9" : size === "lg" ? "w-14 h-14" : "w-11 h-11";
  return (
    <div className={`relative ${dims} rounded-xl overflow-hidden bg-black grid place-items-center shrink-0 ring-1 ring-black/10 ${className}`}>
      <img
        src={LOGO_URL}
        alt="Your Phone Plug logo"
        className="w-full h-full object-contain"
        loading="eager"
      />
    </div>
  );
};
